/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import {
    EnduranceRBACGroupsApiService,
    EnduranceRBACRolesApiService,
    EnduranceRBACUsersApiService
} from '../../../../shared/http';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, ModalService, UserService } from '../../../../../../core/services';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-users',
    templateUrl: './endurance-add-edit-user.component.html',
    styleUrls: ['./endurance-add-edit-user.component.scss'],
})

export class EnduranceAddEditUserComponent extends BaseComponent {
    /**
     * Store the id of the user to edit.
     */
    public editId: string;

    /**
     * Store the data to pass to child.
     */
    public data: any;

    /**
     * User button permissions.
     */
    public buttonReference: any = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private enduranceRBACUsersApiService: EnduranceRBACUsersApiService,
        private enduranceRBACRolesApiService: EnduranceRBACRolesApiService,
        private enduranceRBACGroupsApiService: EnduranceRBACGroupsApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private router: Router,
        private userService: UserService,
    ) {
        super();

        this.editId = this.activatedRoute.snapshot.paramMap.get('id');
        this.setUpPageData();

        this.buttonReference = this.editId
            ? { 'rbac-user-edit': [{ path: '/rbac/users/:id', methods: ['PUT'] }] }
            : { 'rbac-add-user': [{ path: '/rbac/users', methods: ['POST'] }] };
    }

    /**
     * Check for password change before submitting.
     * @param userValues
     */
    public submitUserCheck(userValues: any): void {
        const userId = this.userService.getUser().id;
        const formattedUserValues = this.formatUserValues(userValues);

        if (formattedUserValues.passwordChanged && formattedUserValues.id === userId) {
            this.confirmUserPasswordChange(formattedUserValues);
        } else {
            this.submitUser(formattedUserValues);
        }
    }

    /**
     * Format user values for submission.
     * @param userValues
     * @private
     */
    private formatUserValues(userValues: any): any {
        return {
            id: userValues.step1.id,
            email: userValues.step1.email,
            firstname: userValues.step1.firstName,
            lastname: userValues.step1.lastName,
            password: userValues.step1.password,
            disabled: userValues.step1.disableUser,
            passwordChanged: userValues.passwordChanged,
            roles: this.getIds(userValues.step2.roles),
            groups: this.getIds(userValues.step3.groups)
        };
    }

    /**
     * Get ids.
     * @param values
     * @private
     */
    private getIds(values: any[]): string[] {
        const filteredValues: any[] = values.filter(item => item.value);

        return filteredValues.map((item: any) => item.id);
    }

    /**
     * Confirm user password change.
     * @param usersObject
     * @private
     */
    private confirmUserPasswordChange(usersObject: any): void {
        const confirmationObject = {
            title: 'rbac_users_submit_user',
            message: 'rbac_users_submit_user_warning',
            callBack: this.submitUserAfterChange.bind(this, usersObject, true),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Submit user after password change.
     * @param usersObject
     * @param forceLogout
     * @private
     */
    private submitUserAfterChange(usersObject: any, forceLogout: boolean): Observable<any> {
        return new Observable((): void => {
            this.submitUser(usersObject, forceLogout);
        });
    }

    /**
     * Submit user.
     * @param usersObject
     * @param forceLogOut
     * @private
     */
    private submitUser(usersObject: any, forceLogOut = false): void {
        const call: string = usersObject.id ? 'editUser' : 'addUser';
        usersObject.password = usersObject.password?.length ? usersObject.password : null;

        this.enduranceRBACUsersApiService[call](usersObject)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    if (forceLogOut) {
                        this.userService.logout();
                    } else {
                        const path: string = this.editId ? '../../../' : '../../';
                        this.router.navigate([path], { relativeTo: this.activatedRoute }).then().catch();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EnduranceAddEditUser: submit', error.message);
                },
                complete: (): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                }
            });
    }

    /**
     * Set users, groups, roles.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls: any = {
            users: this.enduranceRBACUsersApiService.getAllUsers(),
            roles: this.enduranceRBACRolesApiService.getAllRoles(),
            groups: this.enduranceRBACGroupsApiService.getAllGroups(),
        };

        if (this.editId) {
            apiCalls['user'] = this.enduranceRBACUsersApiService.getUserById(this.editId);
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    dataGroups['userPatch'] = dataGroups['user'];
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UsersListComponent: getMembers', error.message);
                }
            });
    }
}
